import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { fileService } from '../services/api';

// Función para normalizar tags (igual que en el backend)
const normalizeTag = (tag) => {
  if (!tag || typeof tag !== 'string') return null;
  return tag.toLowerCase().trim().replace(/\s+/g, '-');
};

const TagsModal = ({ isOpen, onClose, file, onTagsUpdated }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [predefinedTags, setPredefinedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [loadingPredefined, setLoadingPredefined] = useState(false);

  // Cargar tags predefinidos cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadPredefinedTags();
    }
  }, [isOpen]);

  // Cargar tags existentes del archivo cuando se abre el modal
  useEffect(() => {
    if (isOpen && file && predefinedTags.length > 0) {
      loadFileTags();
    } else if (!isOpen) {
      setSelectedTags([]);
    }
  }, [isOpen, file, predefinedTags]);

  const loadPredefinedTags = async () => {
    setLoadingPredefined(true);
    try {
      const tags = await fileService.getPredefinedTags();
      setPredefinedTags(tags || []);
    } catch (error) {
      console.error('Error cargando tags predefinidos:', error);
      alert('Error al cargar tags predefinidos: ' + error.message);
    } finally {
      setLoadingPredefined(false);
    }
  };

  const loadFileTags = async () => {
    const fileName = file?.Key || file?.name || '';
    if (!fileName) return;

    setLoadingTags(true);
    try {
      const fileTags = await fileService.getFileTags(fileName);
      // Los tags vienen normalizados del backend, necesitamos mapearlos a los originales
      const normalizedFileTags = fileTags.map(tag => normalizeTag(tag));
      
      // Encontrar los tags originales que coinciden con los normalizados
      const matchingTags = predefinedTags.filter(tag => 
        normalizedFileTags.includes(normalizeTag(tag))
      );
      
      setSelectedTags(matchingTags);
    } catch (error) {
      console.error('Error cargando tags:', error);
      setSelectedTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        // Remover tag
        return prev.filter(t => t !== tag);
      } else {
        // Agregar tag (máximo 6)
        if (prev.length >= 6) {
          alert('Solo se pueden seleccionar máximo 6 tags');
          return prev;
        }
        return [...prev, tag];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fileName = file?.Key || file?.name || '';
      // Enviar los tags seleccionados (se normalizarán en el backend)
      await fileService.updateFileTags(fileName, selectedTags);
      
      if (onTagsUpdated) {
        // Enviar tags normalizados para consistencia
        const normalizedTags = selectedTags.map(tag => normalizeTag(tag));
        onTagsUpdated(fileName, normalizedTags);
      }

      onClose();
    } catch (error) {
      console.error('Error al actualizar tags:', error);
      alert('Error al actualizar tags: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const fileName = file?.displayName || file?.Key || file?.name || '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Editar etiquetas">
      <form onSubmit={handleSubmit} className="space-y-4">
        {loadingPredefined ? (
          <div className="text-sm text-gray-400">Cargando tags disponibles...</div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Selecciona las etiquetas ({selectedTags.length}/6)
              </label>
              
              {loadingTags ? (
                <div className="text-sm text-gray-400 mb-4">Cargando tags del archivo...</div>
              ) : null}
              
              <div className="max-h-96 overflow-y-auto border border-gray-600 rounded-md p-4 bg-gray-800">
                <div className="flex flex-wrap gap-2">
                  {predefinedTags.map((tag) => {
                    const isSelected = selectedTags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => handleTagToggle(tag)}
                        disabled={loading || loadingTags}
                        className={`
                          inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border
                          transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40
                          ${isSelected
                            ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                            : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white hover:border-gray-500'
                          }
                          ${(loading || loadingTags) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {tag}
                        {isSelected && (
                          <span className="ml-2 text-xs">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {selectedTags.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-400 mb-2">Tags seleccionados:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleTagToggle(tag)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          disabled={loading}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading || loadingPredefined}
            className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-600 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || loadingTags || loadingPredefined}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50"
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TagsModal;
